<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { VTextField, SelectCards } from '@/modules/shared/components'
import { useCandidateStore } from '@/modules/connecting/stores/candidate-store'
import { useWorkspaceOnboardingStore } from '../../stores/workspace-onboarding-store'
import { initialMoney, toMoney, toNumber } from '@/modules/shared/utils/money'
import { useExtendedI18n } from '@/i18n'

const { n } = useExtendedI18n()
const candidateStore = useCandidateStore()
const onboardingStore = useWorkspaceOnboardingStore()

const INVESTMENT_TYPES = [
  { value: 'fund', label: 'Fund', description: '' },
  { value: 'syndicate', label: 'Syndicate', description: '' },
  { value: 'angel_group', label: 'Angel Group', description: '' },
]

const form = ref({
  investment_types_interested: [] as string[],
  fund_investment_min: initialMoney,
  spv_investment_min: initialMoney,
  deal_investment_min: initialMoney,
})

const isFundSelected = computed(() => form.value.investment_types_interested.includes('fund'))
const isSyndicateSelected = computed(() => form.value.investment_types_interested.includes('syndicate'))
const isAngelGroupSelected = computed(() => form.value.investment_types_interested.includes('angel_group'))

const candidate = computed(() => {
  const id = onboardingStore.workspace?.connecting_candidate_id
  return id ? candidateStore.getCandidate(id.toString()) : undefined
})

watch(
  candidate,
  (c) => {
    if (!c) return
    form.value.investment_types_interested = c.investment_types_interested || []
    form.value.fund_investment_min = c.fund_investment_min ? toMoney(c.fund_investment_min, 'USD') : initialMoney
    form.value.spv_investment_min = c.spv_investment_min ? toMoney(c.spv_investment_min, 'USD') : initialMoney
    form.value.deal_investment_min = c.deal_investment_min ? toMoney(c.deal_investment_min, 'USD') : initialMoney
  },
  { immediate: true },
)

const submit = async (): Promise<boolean> => {
  const candidateId = onboardingStore.workspace?.connecting_candidate_id
  if (!candidateId) return false

  await candidateStore.updateCandidate({
    id: candidateId,
    subject_id: onboardingStore.workspace!.id,
    subject_type: 'Group',
    investment_types_interested: form.value.investment_types_interested,
    fund_investment_min: isFundSelected.value ? toNumber(form.value.fund_investment_min) : null,
    spv_investment_min: isSyndicateSelected.value ? toNumber(form.value.spv_investment_min) : null,
    deal_investment_min: isAngelGroupSelected.value ? toNumber(form.value.deal_investment_min) : null,
  })
  return true
}

defineExpose({ submit })
</script>

<template>
  <div class="flex flex-1 flex-col">
    <SelectCards v-model="form.investment_types_interested" :options="INVESTMENT_TYPES" :multiple="true" :columns="3" />

    <div v-if="form.investment_types_interested.length" class="mt-7 flex flex-col gap-4">
      <VTextField
        type="currency"
        v-if="isFundSelected"
        v-model="form.fund_investment_min"
        label="Minimum investor commitment per fund"
        :placeholder="n(initialMoney, 'currency')"
        align="left"
        inputClass="!bg-white"
      />
      <VTextField
        type="currency"
        v-if="isSyndicateSelected"
        v-model="form.spv_investment_min"
        label="Minimum investor commitment per syndicate (SPV)"
        :placeholder="n(initialMoney, 'currency')"
        align="left"
        inputClass="!bg-white"
      />
      <VTextField
        type="currency"
        v-if="isAngelGroupSelected"
        v-model="form.deal_investment_min"
        label="Minimum investor commitment per deal"
        :placeholder="n(initialMoney, 'currency')"
        align="left"
        inputClass="!bg-white"
      />
    </div>
  </div>
</template>
